import { defineMessages } from 'react-intl'

import { ProviderPairAccess } from './types'

import type { ProviderPairProviderProfile } from './providerPairUtils'

// UI status supplied only when care-history metadata identifies the counterpart’s state.
// This is intentionally separate from messaging access and does not control send permission.
export const ProviderPairCounterpartStatus = {
  INACTIVE: 'inactive',
  UNAVAILABLE: 'unavailable',
  DEACTIVATED: 'deactivated',
} as const

export type ProviderPairCounterpartStatus =
  (typeof ProviderPairCounterpartStatus)[keyof typeof ProviderPairCounterpartStatus]

export const getProviderPairCounterpartStatus = (
  profile: Pick<ProviderPairProviderProfile, 'status' | 'relationshipStatus'>,
): ProviderPairCounterpartStatus | undefined => {
  if (
    profile.status === ProviderPairCounterpartStatus.UNAVAILABLE ||
    profile.status === ProviderPairCounterpartStatus.DEACTIVATED
  ) {
    return profile.status
  }
  return profile.relationshipStatus === 'historical' ? ProviderPairCounterpartStatus.INACTIVE : undefined
}

const messages = defineMessages({
  inactiveBadge: {
    defaultMessage: 'INACTIVE',
    description: 'Badge for a provider with a historical care relationship to this client',
  },
  inactiveTooltip: {
    defaultMessage: 'This provider is no longer part of this client’s active care.',
    description: 'Tooltip explaining an inactive provider care relationship',
  },
  unavailableBadge: {
    defaultMessage: 'UNAVAILABLE',
    description: 'Badge for an unavailable provider',
  },
  deactivatedBadge: {
    defaultMessage: 'DEACTIVATED',
    description: 'Badge for a deactivated provider account',
  },
  unavailableHeaderNote: {
    defaultMessage: 'Provider is unable to be reached right now',
    description: 'Header note for an unavailable provider in a read-only provider-to-provider thread',
  },
  deactivatedHeaderNote: {
    defaultMessage: 'Provider’s account is deactivated.',
    description: 'Header note for a deactivated provider in a read-only provider-to-provider thread',
  },
  deactivatedFooter: {
    defaultMessage: 'This is a read-only thread. The provider’s Lyra account is deactivated.',
    description: 'Footer replacing the composer when a provider account is deactivated',
  },
  readOnlyFooter: {
    defaultMessage: 'This is a read-only thread.',
    description: 'Footer replacing the composer in a read-only provider-to-provider thread',
  },
})

export const getProviderPairPresentation = (
  access: ProviderPairAccess | undefined,
  status: ProviderPairCounterpartStatus | undefined,
) => {
  if (access !== ProviderPairAccess.READ) {
    return { headerNote: undefined, readOnlyFooter: undefined }
  }

  return {
    headerNote:
      status === ProviderPairCounterpartStatus.UNAVAILABLE
        ? messages.unavailableHeaderNote
        : status === ProviderPairCounterpartStatus.DEACTIVATED
          ? messages.deactivatedHeaderNote
          : undefined,
    readOnlyFooter:
      status === ProviderPairCounterpartStatus.DEACTIVATED ? messages.deactivatedFooter : messages.readOnlyFooter,
  }
}

// Lifecycle labels describe the counterpart; conversation capabilities alone authorize sending.
export const getProviderPairBadgePresentation = (status: ProviderPairCounterpartStatus | undefined) => {
  switch (status) {
    case ProviderPairCounterpartStatus.INACTIVE:
      return { label: messages.inactiveBadge, tooltip: messages.inactiveTooltip }
    case ProviderPairCounterpartStatus.UNAVAILABLE:
      return { label: messages.unavailableBadge, tooltip: undefined }
    case ProviderPairCounterpartStatus.DEACTIVATED:
      return { label: messages.deactivatedBadge, tooltip: undefined }
    default:
      return undefined
  }
}
